/**
 * Session tabs context + hook. Tracks the open-tabs strip rendered in the
 * bottom bar (SessionTabsBar). Tabs have stable id-based identity:
 * - Dashboard: id = 'dashboard' (deterministic, pinned).
 * - Non-session routes: id = path (one tab per pathname).
 * - Buying sessions: id = 'session-' + sessionId, with the buying_sessions
 *   uuid in `sessionId`. Two distinct buying sessions live as two distinct
 *   tabs even though they share a pathname; the query string disambiguates.
 *
 * Auto-add on navigation skips /purchasing/buying-session entirely — those
 * tabs are added explicitly via openBuyingSessionTab(). Without this, two
 * sessions on the same pathname collide on the auto-add dedupe.
 */

import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  createContext,
  useContext,
  type JSX,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ── Local types ────────────────────────────────────────────────────────────

interface SessionTab {
  id: string;
  label: string;
  path: string;
  pinned: boolean;
  sessionId?: string;
}

export interface UseSessionTabsResult {
  tabs: SessionTab[];
  activeTabId: string | null;
  openTab: (path: string) => void;
  openBuyingSessionTab: (sessionId: string, label: string) => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  updateTabLabel: (id: string, label: string) => void;
}

// ── Tab label resolver ──────────────────────────────────────────────────────

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/tasks': 'Tasks',
  '/files': 'Files',
  '/catalog/products': 'Products',
  '/catalog/flagged': 'Flagged',
  '/purchasing': 'Purchasing',
  '/purchasing/buying-session': 'Buying Session',
  '/purchasing/po-builder': 'PO Builder',
  '/purchasing/purchase-orders': 'Purchase Orders',
  '/purchasing/receiving': 'Receiving',
  '/purchasing/discrepancies': 'Purchasing Discrepancies',
  '/inventory': 'Stock Visibility',
  '/inventory/restock': 'Restock',
  '/inventory/reconciliation': 'Inventory Reconciliation',
  '/listings': 'Listings',
  '/listings/review': 'Listings Review',
  '/fulfillment': 'Fulfillment',
  '/fulfillment/orders': 'Orders',
  '/fulfillment/channel-fulfilled': 'Channel Fulfilled',
  '/fulfillment/review': 'Fulfillment Review',
  '/fulfillment/verify': 'Verify',
  '/fulfillment/fulfill': 'Fulfill',
  '/fulfillment/pick': 'Pick',
  '/fulfillment/ship': 'Ship',
  '/fulfillment/awaiting-label': 'Awaiting Label',
  '/fulfillment/awaiting-pickup': 'Awaiting Pickup',
  '/fulfillment/pto': 'PTO',
  '/fulfillment/on-hold': 'On Hold',
  '/fulfillment/followups': 'Followups',
  '/fulfillment/discrepancies': 'Fulfillment Discrepancies',
  '/returns': 'Returns',
  '/returns/inspection': 'Inspection',
  '/returns/disposition': 'Disposition',
  '/returns/discrepancies': 'Returns Discrepancies',
  '/transfers/inbound': 'Inbound',
  '/transfers/removals': 'Removals',
  '/finance/credit-cash': 'Credit & Cash',
  '/finance/invoices': 'Invoices',
  '/finance/payouts': 'Payouts',
  '/finance/reconciliation': 'Finance Reconciliation',
  '/finance/expenses': 'Expenses',
  '/finance/payroll': 'Payroll',
  '/finance/review': 'Finance Review',
  '/reports': 'Reports',
  '/directory/distributors': 'Distributors',
  '/directory/suppliers': 'Suppliers',
  '/directory/brands': 'Brands',
  '/directory/categories': 'Categories',
  '/directory/marketplaces': 'Marketplaces',
  '/directory/warehouses': 'Warehouses',
  '/settings': 'Settings',
};

function labelForPath(path: string): string {
  return ROUTE_LABELS[path] || path.split('/').filter(Boolean).pop() || 'Page';
}

// ── Active-tab resolution ──────────────────────────────────────────────────

const BUYING_SESSION_PATH = '/purchasing/buying-session';
const SESSION_TAB_ID_PREFIX = 'session-';

function resolveActiveTabId(tabs: SessionTab[], pathname: string, search: string): string | null {
  const fullUrl = pathname + search;
  const exact = tabs.find((t) => t.path === fullUrl);
  if (exact) return exact.id;
  const pathOnly = tabs.find((t) => t.path === pathname);
  return pathOnly?.id ?? null;
}

// ── Context ─────────────────────────────────────────────────────────────────

const SessionTabsContext = createContext<UseSessionTabsResult | null>(null);

export function SessionTabsProvider({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState<SessionTab[]>([
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard', pinned: true },
  ]);

  const activeTabId = useMemo(
    () => resolveActiveTabId(tabs, location.pathname, location.search),
    [tabs, location.pathname, location.search]
  );

  // Auto-add a tab for the current pathname. Buying-session paths are
  // excluded — those tabs are added explicitly via openBuyingSessionTab so
  // multiple ?session=<uuid> URLs on the same pathname don't collide.
  useEffect(() => {
    const path = location.pathname;
    if (path === BUYING_SESSION_PATH) return;
    setTabs((prev) => {
      if (prev.some((t) => t.id === path)) return prev;
      return [...prev, { id: path, label: labelForPath(path), path, pinned: false }];
    });
  }, [location.pathname]);

  const openTab = useCallback(
    (path: string): void => {
      setTabs((prev) => {
        if (prev.some((t) => t.id === path)) return prev;
        return [...prev, { id: path, label: labelForPath(path), path, pinned: false }];
      });
      navigate(path);
    },
    [navigate]
  );

  const openBuyingSessionTab = useCallback(
    (sessionId: string, label: string): void => {
      const id = SESSION_TAB_ID_PREFIX + sessionId;
      const path = `${BUYING_SESSION_PATH}?session=${sessionId}`;
      setTabs((prev) => {
        const existing = prev.find((t) => t.id === id);
        if (existing) {
          if (existing.label === label) return prev;
          return prev.map((t) => (t.id === id ? { ...t, label } : t));
        }
        return [...prev, { id, label, path, pinned: false, sessionId }];
      });
      navigate(path);
    },
    [navigate]
  );

  const closeTab = useCallback(
    (id: string): void => {
      const target = tabs.find((t) => t.id === id);
      if (!target || target.pinned) return;
      const filtered = tabs.filter((t) => t.id !== id);
      setTabs(filtered);
      // navigate() stays outside the setTabs updater — React can run updaters
      // during render, and a router update mid-render is setState-in-render.
      if (id === activeTabId && filtered.length > 0) {
        navigate(filtered[filtered.length - 1].path);
      }
    },
    [tabs, activeTabId, navigate]
  );

  const switchTab = useCallback(
    (id: string): void => {
      const target = tabs.find((t) => t.id === id);
      if (target) navigate(target.path);
    },
    [tabs, navigate]
  );

  const updateTabLabel = useCallback((id: string, label: string): void => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, label } : t)));
  }, []);

  return (
    <SessionTabsContext.Provider
      value={{
        tabs,
        activeTabId,
        openTab,
        openBuyingSessionTab,
        closeTab,
        switchTab,
        updateTabLabel,
      }}
    >
      {children}
    </SessionTabsContext.Provider>
  );
}

// Provider + hook intentionally co-located in this file to keep a single
// import path for consumers; react-refresh prefers component-only files but
// splitting the hook out would just churn imports across the app.
// eslint-disable-next-line react-refresh/only-export-components
export function useSessionTabs(): UseSessionTabsResult {
  const ctx = useContext(SessionTabsContext);
  if (!ctx) throw new Error('useSessionTabs must be used inside SessionTabsProvider');
  return ctx;
}
